/**
 * Day / opening-hours shapes used by:
 * - the `companyLocations` Payload Global (authoritative data)
 * - `generateOrganizationSchema.ts` (JSON-LD `openingHoursSpecification`)
 * - the visible `LocationItem` component (formatOpeningHours output)
 *
 * Editorial data lives in the CMS Global, NOT in this file. This module
 * provides only types and a pure formatter.
 */

export type DayOfWeek =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday'

export type OpeningHoursSlot = {
  days: DayOfWeek[]
  opens: string // "HH:MM"
  closes: string // "HH:MM"
}

/** German short labels for weekdays used in the visible opening-hours table. */
export const DAY_LABELS_DE: Record<DayOfWeek, string> = {
  Monday: 'Mo',
  Tuesday: 'Di',
  Wednesday: 'Mi',
  Thursday: 'Do',
  Friday: 'Fr',
  Saturday: 'Sa',
  Sunday: 'So',
}

/**
 * Renders an opening-hours slot list as compact day-range strings.
 * Example:
 *   [{ days: [Mo..Fr], 07:00–12:00 }, { days: [Mo..Fr], 13:00–17:00 }]
 * → ["Mo–Fr 07:00–12:00, 13:00–17:00"]
 */
export function formatOpeningHours(slots: OpeningHoursSlot[]): string[] {
  const ORDER: DayOfWeek[] = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ]
  const dayKey = (days: DayOfWeek[]) =>
    [...days].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b)).join(',')

  const byDays = new Map<string, { days: DayOfWeek[]; times: string[] }>()
  for (const slot of slots) {
    const key = dayKey(slot.days)
    const time = `${slot.opens}–${slot.closes}`
    const existing = byDays.get(key)
    if (existing) {
      existing.times.push(time)
    } else {
      byDays.set(key, { days: slot.days, times: [time] })
    }
  }

  const formatDays = (days: DayOfWeek[]): string => {
    const sorted = [...days].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))
    if (sorted.length === 1) return DAY_LABELS_DE[sorted[0]]
    const indices = sorted.map((d) => ORDER.indexOf(d))
    const isContiguous = indices.every((v, i) => i === 0 || v === indices[i - 1] + 1)
    if (isContiguous && sorted.length >= 2) {
      return `${DAY_LABELS_DE[sorted[0]]}–${DAY_LABELS_DE[sorted[sorted.length - 1]]}`
    }
    return sorted.map((d) => DAY_LABELS_DE[d]).join(', ')
  }

  return Array.from(byDays.values()).map(
    ({ days, times }) => `${formatDays(days)} ${times.join(', ')}`,
  )
}
